import { useState, useEffect, useRef } from 'react';
import { FaEllipsisV, FaTrashAlt } from 'react-icons/fa';
import { Images } from '../../../utils/imageConstants';
import type { IMember } from '../types/myTeam';

interface MemberCardProps {
  member: IMember;
  onRemove?: (playerId: string) => void;
}

function getRoleIcons(role?: string): React.ReactNode[] {
  const r = (role ?? '').toLowerCase().trim();

  // wk-Batter, Wicketkeeper – gloves icon
  if (r && (r.includes('wk') || r.includes('wicket') || r.includes('keeper'))) {
    return [<img key="gloves" src={Images.hands_icon} alt="" className="myteam-member-role-icon" />];
  }

  // All-Rounder – bat + ball
  if (r && (r.includes('all-rounder') || r.includes('allrounder'))) {
    return [
      <img key="bat" src={Images.bat_icon} alt="" className="myteam-member-role-icon" />,
      <img key="ball" src={Images.ball_icon} alt="" className="myteam-member-role-icon" />,
    ];
  }

  // Batter, Batsman
  if (r && (r.includes('batter') || r.includes('batsman'))) {
    return [<img key="bat" src={Images.bat_icon} alt="" className="myteam-member-role-icon" />];
  }

  // Bowler
  if (r && r.includes('bowler')) {
    return [<img key="ball" src={Images.ball_icon} alt="" className="myteam-member-role-icon" />];
  }

  // Fallback – always show bat icon (including for empty/Player role)
  return [<img key="bat-def" src={Images.bat_icon} alt="" className="myteam-member-role-icon" />];
}

function MemberCard({ member, onRemove }: MemberCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const fullName = `${member.playerFirstName} ${member.playerLastName}`;
  const roleIcons = getRoleIcons(member.role);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleRemove = () => {
    onRemove?.(member.playerId);
    setMenuOpen(false);
  };

  return (
    <div className="myteam-member-card">
      <div className="myteam-member-card-top">
        <div className="myteam-member-badges">
          {member.isCaptain && (
            <span className="myteam-member-badge myteam-member-badge-c">
              <img src={Images.c_icon} alt="C" className="myteam-badge-img" />
            </span>
          )}
          {member.isViceCaptain && (
            <span className="myteam-member-badge myteam-member-badge-vc">
              <img src={Images.vc_icon} alt="VC" className="myteam-badge-img" />
            </span>
          )}
          <div className="myteam-member-role-icons">
            {roleIcons}
          </div>
        </div>
        <div className="myteam-member-menu-wrap" ref={menuRef}>
            <button
              type="button"
              className="myteam-member-menu"
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label="Options"
              aria-expanded={menuOpen}
            >
              <FaEllipsisV />
            </button>
            {menuOpen && (
              <div className="myteam-member-dropdown">
                <button
                  type="button"
                  className="myteam-member-dropdown-remove"
                  onClick={handleRemove}
                >
                  <FaTrashAlt />
                  <span>Remove</span>
                </button>
              </div>
            )}
          </div>
      </div>
      <div className="myteam-member-avatar-wrap">
        <img
          src={member.avatar || Images.player_image}
          alt={fullName}
          className="myteam-member-avatar"
        />
      </div>
      <div className="myteam-member-info">
        <span className="myteam-member-name">{fullName}</span>
        <span className="myteam-member-role">{member.role ?? 'Player'}</span>
      </div>
    </div>
  );
}

export default MemberCard;
